import { useMemo, useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DateRangeValue {
  dateFrom: string | null;
  dateTo: string | null;
}

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

interface DatePreset {
  label: string;
  getRange: () => { from: string; to: string };
}

function formatDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function makePresets(): DatePreset[] {
  const today = new Date();
  return [
    {
      label: 'Today',
      getRange: () => ({ from: formatDate(today), to: formatDate(today) }),
    },
    {
      label: 'Yesterday',
      getRange: () => {
        const d = subDays(today, 1);
        return { from: formatDate(d), to: formatDate(d) };
      },
    },
    {
      label: 'Last 7 Days',
      getRange: () => ({ from: formatDate(subDays(today, 6)), to: formatDate(today) }),
    },
    {
      label: 'Last 30 Days',
      getRange: () => ({ from: formatDate(subDays(today, 29)), to: formatDate(today) }),
    },
    {
      label: 'This Month',
      getRange: () => ({
        from: formatDate(startOfMonth(today)),
        to: formatDate(today),
      }),
    },
    {
      label: 'Last Month',
      getRange: () => {
        const lastMonth = subMonths(today, 1);
        return {
          from: formatDate(startOfMonth(lastMonth)),
          to: formatDate(endOfMonth(lastMonth)),
        };
      },
    },
  ];
}

function displayLabel(value: DateRangeValue): string {
  if (!value.dateFrom && !value.dateTo) return 'All Dates';
  const from = value.dateFrom ? format(new Date(value.dateFrom + 'T00:00:00'), 'MMM d, yyyy') : '...';
  const to = value.dateTo ? format(new Date(value.dateTo + 'T00:00:00'), 'MMM d, yyyy') : '...';
  if (value.dateFrom === value.dateTo) return from;
  return `${from} – ${to}`;
}

function toCalendarRange(value: DateRangeValue): DateRange | undefined {
  if (!value.dateFrom && !value.dateTo) return undefined;
  return {
    from: value.dateFrom ? new Date(value.dateFrom + 'T00:00:00') : undefined,
    to: value.dateTo ? new Date(value.dateTo + 'T00:00:00') : undefined,
  };
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(
    toCalendarRange(value),
  );
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const presets = useMemo(() => makePresets(), []);
  const hasFilter = value.dateFrom !== null || value.dateTo !== null;

  function handlePresetClick(preset: DatePreset) {
    const range = preset.getRange();
    setActivePreset(preset.label);
    setPendingRange({
      from: new Date(range.from + 'T00:00:00'),
      to: new Date(range.to + 'T00:00:00'),
    });
  }

  function handleCalendarSelect(range: DateRange | undefined) {
    setActivePreset(null);
    setPendingRange(range);
  }

  function handleApply() {
    if (pendingRange?.from) {
      onChange({
        dateFrom: formatDate(pendingRange.from),
        dateTo: pendingRange.to ? formatDate(pendingRange.to) : formatDate(pendingRange.from),
      });
    } else {
      onChange({ dateFrom: null, dateTo: null });
    }
    setOpen(false);
  }

  function handleClear() {
    setPendingRange(undefined);
    setActivePreset(null);
    onChange({ dateFrom: null, dateTo: null });
    setOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setPendingRange(toCalendarRange(value));
      setActivePreset(null);
    }
    setOpen(nextOpen);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full sm:w-auto justify-start gap-2 text-left font-normal h-9',
            hasFilter && 'border-primary text-primary',
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{displayLabel(value)}</span>
          {hasFilter && (
            <XIcon
              className="h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Presets panel */}
          <div className="flex flex-col gap-0.5 border-r p-2 min-w-[140px]">
            <span className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Quick Select
            </span>
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={cn(
                  'text-left text-sm px-2 py-1.5 rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                  activePreset === preset.label && 'bg-accent text-accent-foreground font-medium',
                )}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {/* Calendar panel */}
          <div className="p-2">
            <Calendar
              mode="range"
              selected={pendingRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={1}
              defaultMonth={pendingRange?.from}
            />
            <div className="flex justify-end gap-2 border-t pt-2 px-1">
              <Button variant="ghost" size="sm" onClick={handleClear}>
                Clear
              </Button>
              <Button size="sm" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
